import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';

const SubscriptionPage = ({ user }) => {
  const [isAnnual, setIsAnnual] = useState(false);

  // Calculate prices and savings
  const prices = {
    standard: {
      monthly: 14.99,
      annual: 149.99,
      savings: Math.round((14.99 * 12 - 149.99) / (14.99 * 12) * 100)
    },
    pro: {
      monthly: 29.99,
      annual: 299.99,
      savings: Math.round((29.99 * 12 - 299.99) / (29.99 * 12) * 100)
    }
  };

  const handleSubscribe = (plan) => {
    if (!user || !user.id) {
      console.error('No user ID found');
      return;
    }

    // Add client_reference_id and customer email to the URL
    const baseUrl = plan === 'standard' 
      ? 'https://buy.stripe.com/4gw6qr5DQ27UcmYcMN'
      : 'https://buy.stripe.com/3cs4ij2rE27Udr27ss';

    // Add client_reference_id and prefill customer email
    const url = new URL(baseUrl);
    url.searchParams.append('client_reference_id', user.id);
    if (user.email) {
      url.searchParams.append('prefilled_email', user.email);
    }

    window.location.href = url.toString();
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400">
              Choose Your Plan
            </span>
          </h2>
          <p className="text-xl text-gray-800 dark:text-gray-200 max-w-2xl mx-auto">
            Start with a 3-day free trial. No credit card required.
          </p>
        </div>

        {/* Pricing toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white/90 dark:bg-gray-800/90 rounded-full p-1 inline-flex items-center shadow-lg">
            <button 
              onClick={() => setIsAnnual(false)}
              className={`px-6 py-2 rounded-full font-medium transition-colors ${
                !isAnnual 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Monthly
            </button>
            <button 
              onClick={() => setIsAnnual(true)}
              className={`px-6 py-2 rounded-full font-medium transition-colors ${
                isAnnual 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Annually
            </button>
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid sm:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Standard Plan */}
          <div className="relative bg-white/95 dark:bg-gray-800/95 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
            {/* Trial Badge */}
            <div className="absolute -top-4 right-4">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-1 rounded-full text-sm font-medium shadow-lg">
                3-day free trial
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Standard</h3>
              <div className="flex items-baseline">
                <span className="text-5xl font-bold text-gray-900 dark:text-white">
                  ${isAnnual ? prices.standard.annual : prices.standard.monthly}
                </span>
                <span className="text-gray-500 dark:text-gray-400 ml-2">
                  /{isAnnual ? 'year' : 'month'}
                </span>
                {isAnnual && (
                  <span className="ml-2 px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-sm rounded-full">
                    Save {prices.standard.savings}%
                  </span>
                )}
              </div>
              <p className="text-gray-600 dark:text-gray-400 mt-4">Perfect for individuals and small teams</p>
            </div>

            <ul className="space-y-4 mb-8">
              {[
                "50 documents per month",
                "100MB per document max file size",
                "Support for PPTX, Word, PDF, Spreadsheet",
                "Advanced analytics",
                "Multilingual documents/tables",
                "Basic OCR",
                "Standard support"
              ].map((feature) => (
                <li key={feature} className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                </li>
              ))}
            </ul>

            <button 
              onClick={() => handleSubscribe('standard')}
              className="w-full py-3 px-6 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              Start Free Trial
            </button>
          </div>

          {/* Pro Plan */}
          <div className="relative bg-gradient-to-b from-purple-600 to-indigo-600 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
            {/* Trial Badge */}
            <div className="absolute -top-4 right-4">
              <div className="bg-white text-purple-600 px-4 py-1 rounded-full text-sm font-medium shadow-lg">
                3-day free trial
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">Pro</h3>
              <div className="flex items-baseline">
                <span className="text-5xl font-bold text-white">
                  ${isAnnual ? prices.pro.annual : prices.pro.monthly}
                </span>
                <span className="text-purple-200 ml-2">
                  /{isAnnual ? 'year' : 'month'}
                </span>
                {isAnnual && (
                  <span className="ml-2 px-2 py-1 bg-white/20 text-white text-sm rounded-full">
                    Save {prices.pro.savings}%
                  </span>
                )}
              </div>
              <p className="text-purple-100 mt-4">For power users and teams</p>
            </div>

            <ul className="space-y-4 mb-8">
              {[
                "Unlimited documents per month",
                "100MB per document max file size",
                "Support for all document types",
                "Premium OCR & data extraction",
                "Advanced analytics",
                "Priority support",
                "API access",
                "Custom folders"
              ].map((feature) => (
                <li key={feature} className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-purple-200 mr-3" />
                  <span className="text-white">{feature}</span>
                </li>
              ))}
            </ul>

            <button 
              onClick={() => handleSubscribe('pro')}
              className="w-full py-3 px-6 rounded-xl bg-white text-purple-600 font-medium hover:bg-purple-50 transition-colors"
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;